'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { XIcon } from 'lucide-react';
import { Button } from './button';
import { cn } from './lib/utils';
import {
  suppressScrollAdjustments,
  restoreScrollPositionAfterLock,
} from './lib/suppress-scroll-adjustments';

// Radix's DialogContent focus-traps into the panel on open (FocusScope's
// own focus() calls are preventScroll-safe, but nested content — a
// SelectField, an autoFocus input, anything with its own scrollIntoView/
// focus behavior — isn't). When the dialog's trigger sits below the fold,
// that reads as "the page jumps to the top" on open. See
// `suppressScrollAdjustments` (./lib) for that fix — plus
// `restoreScrollPositionAfterLock` (./lib) for a second, independent jump
// source: the background-scroll lock itself (react-remove-scroll's
// `body[data-scroll-locked]`) clamps `window.scrollY` to 0 via a
// browser-internal reflow the instant it engages, with no observable
// focus/scrollIntoView/scrollTo call — confirmed by scripted DevTools
// instrumentation.
//
// The scroll snapshot for that second fix has to be taken during *render*,
// not inside our own useLayoutEffect: react-remove-scroll's lock effect
// lives on a descendant (DialogContent → RemoveScroll), and React fires
// child layout effects before parent ones in the same commit — so by the
// time this component's useLayoutEffect would run, the clamp has already
// happened and `window.scrollY` is already 0. Render happens before every
// effect in the commit, so it's the only point guaranteed to see the
// pre-clamp value.
//
// Both fixes have to be wired to the *resolved* `open` boolean, not to
// onOpenChange: most call sites in this app use Dialog fully controlled
// (`open={state}` set from their own button's onClick, no DialogTrigger) —
// Radix only invokes onOpenChange for changes *it* initiates (Escape,
// overlay click), never for an externally-driven open=true, so an
// onOpenChange-only patch silently never armed for those.
function Dialog({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const open = openProp ?? internalOpen;
  const restoreRef = React.useRef<(() => void) | null>(null);

  const wasOpenRef = React.useRef(open);
  const openScrollRef = React.useRef<{ x: number; y: number } | null>(null);
  if (open && !wasOpenRef.current && typeof window !== 'undefined') {
    openScrollRef.current = { x: window.scrollX, y: window.scrollY };
  }
  wasOpenRef.current = open;

  React.useLayoutEffect(() => {
    restoreRef.current?.();
    if (open) {
      const restoreFocusPatch = suppressScrollAdjustments();
      const cancelScrollRestore = openScrollRef.current
        ? restoreScrollPositionAfterLock(openScrollRef.current)
        : () => {};
      restoreRef.current = () => {
        restoreFocusPatch();
        cancelScrollRestore();
      };
    } else {
      restoreRef.current = null;
    }
    return () => {
      restoreRef.current?.();
      restoreRef.current = null;
    };
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (openProp === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      {...props}
    />
  );
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        // No backdrop-filter on this overlay — confirmed root cause of the
        // sticky site header vanishing while a Dialog is open: several
        // header variants (GlassHeader, CinematicHeader in
        // packages/site-builder/runtime/src/shells/headers/index.tsx) are
        // themselves `position: sticky` with their own `backdrop-filter:
        // blur(...)` for their glass look. Two composited `backdrop-filter`
        // layers stacked on top of each other hits a Chromium compositing
        // bug where the earlier (header) layer paints fully transparent
        // until an unrelated repaint forces a re-composite — reproduced
        // consistently in a real browser, not headless. Dropping blur from
        // the side we control (this overlay) removes the double-blur
        // trigger without touching the header's glass design.
        'fixed inset-0 isolate z-[100] bg-(--color-neutral-950,#0a0a0a)/20 duration-100 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        'supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  const { t } = useTranslation('ui');
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'fixed top-1/2 left-1/2 z-[101] grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-6 rounded-xl bg-popover p-6 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-xl data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button variant="ghost" className="absolute top-4 right-4" size="icon-sm">
              <XIcon />
              <span className="sr-only">{t('common:actions.close')}</span>
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  const { t } = useTranslation('ui');
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">{t('common:actions.close')}</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-heading leading-none font-medium', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
